interface GenerateRequest {
  aspect_ratio?: string;
  duration?: number;
  keep_original_sound?: boolean;
  resolution?: string;
  prompt?: string;
  model_id_list?: string[];
  mode_type?: number;
  reference_mode?: number;
  ref_videos?: string[];
  ref_images?: string[];
  ref_audios?: string[];
  /** 多模态参考文档（Wan3.0）：asset_type=Link 传 url，asset_type=File 传 asset_id */
  media?: Array<{ asset_type: 'Link' | 'File'; url?: string; asset_id?: string }>;
  number?: number;
  meta_list?: MetaItem[];
}

interface MetaItem {
  meta_type: 'text' | 'image' | 'video' | 'audio';
  text?: string;
  material_ref?: { material_idx: number };
}

// 匹配 [@图片1:ID] / [@视频2:ID] / [@音频1:ID]
const MENTION_RE = /(\[@(?:图片|视频|音频)\d+:[^\]]+\])/g;
const MENTION_DETAIL_RE = /^\[@(图片|视频|音频)(\d+):([^\]]+)\]$/;

const TYPE_MAP: Record<string, 'image' | 'video' | 'audio'> = {
  图片: 'image',
  视频: 'video',
  音频: 'audio',
};  

//根据区分模型 -- 处理提交格式数据
/**
 * 将 prompt 中的 [@类型N:ID] 解析为 meta_list，无需外部查表：
 *   - meta_type  : 从中文前缀推导（图片→image / 视频→video / 音频→audio）
 *   - material_idx: N - 1（视频1 → 0，视频2 → 1，依此类推，按类型独立计数）
 *   - 发给后端的 prompt 自动清洗为 "@视频1" 纯文本格式
 *
 * @param data  原始请求（prompt 含 [@类型N:ID] 格式）
 */
export const maintainData = (data: GenerateRequest): GenerateRequest => {
  const prompt = data.prompt || '';

  // 按 mention 切割 prompt
  const segments = prompt.split(MENTION_RE).filter(Boolean);

  const meta_list: MetaItem[] = [];

  for (const seg of segments) {
    const m = seg.match(MENTION_DETAIL_RE);
    if (m) {
      const [, typeCN, numStr] = m;
      const type = TYPE_MAP[typeCN];
      const material_idx = parseInt(numStr, 10) - 1; // "视频1" → 0, "图片2" → 1

      meta_list.push({
        meta_type: type,
        text: '',
        material_ref: { material_idx },
      });
    } else {
      // 普通文本段（去掉首尾空白，保留内容）
      const text = seg.replace(/^\s+|\s+$/g, '');
      if (text) {
        meta_list.push({ meta_type: 'text', text });
      }
    }
  }

  // 清洗 prompt：把 [@视频1:ID] → @视频1，去掉方括号和ID
  // const cleanPrompt = prompt
  //   .replace(/\[@((?:图片|视频|音频)\d+):[^\]]+\]/g, '@$1')
  //   .replace(/\s+$/, '');

  // 多模态（含 mention）时 prompt 传空串，否则正常传递
  const isMultiModal = meta_list.some(item => item.meta_type !== 'text');
  return { ...data, prompt: isMultiModal ? '' : prompt, meta_list };
};

const TYPE_CN_MAP: Record<string, string> = {
  image: '图片',
  video: '视频',
  audio: '音频',
};

/**
 * 将 meta_list 反向拼接为带 [@类型N:ID] 格式的 prompt 显示字符串
 * 用于从 feed/list 接口返回的 video_payload 中恢复 prompt 展示内容
 *
 * @param meta_list   video_payload.meta_list
 * @param ref_images  video_payload.ref_images  (按 material_idx 取 ID)
 * @param ref_videos  video_payload.ref_videos
 * @param ref_audios  video_payload.ref_audios
 */
export const reconstructPromptFromMetaList = (
  meta_list: MetaItem[],
  ref_images: string[] = [],
  ref_videos: string[] = [],
  ref_audios: string[] = [],
): string => {
  return meta_list
    .map(item => {
      if (item.meta_type === 'text') return item.text || '';

      const idx = item.material_ref?.material_idx ?? 0;
      let id = '';
      if (item.meta_type === 'image') id = ref_images[idx] || '';
      else if (item.meta_type === 'video') id = ref_videos[idx] || '';
      else if (item.meta_type === 'audio') id = ref_audios[idx] || '';

      const typeCN = TYPE_CN_MAP[item.meta_type] ?? '';
      return `[@${typeCN}${idx + 1}:${id}]`;
    })
    .join('');
};

// 匹配裸的 [@图片1] / [@视频2] / [@音频1] （不含冒号和ID）
const BARE_MENTION_RE = /\[@(图片|视频|音频)(\d+)\]/g;

/**
 * 将内部格式 [@类型N:ID] 转换为不带 @ 的纯标签文本，如 [@图片1:xxx] → 图片1。
 * 用于只读展示与复制：素材以缩略图呈现，文字保留「图片1」这类指代，但不带 @ 符号。
 */
export const formatPromptMentionLabel = (raw: string): string =>
  raw.replace(/\[@((?:图片|视频|音频)\d+):[^\]]*\]/g, '$1');

/**
 * 当 meta_list 不存在但 prompt 中包含裸的 [@类型N] 格式时，
 * 用 ref_images / ref_videos / ref_audios 按 N 的顺序补全 ID。
 *
 * 例如： "轮船[@图片1]巨浪" + ref_images=["abc123"]
 *       → "轮船[@图片1:abc123]巨浪"
 */
export const enrichPromptWithRefIds = (
  prompt: string,
  ref_images: string[] = [],
  ref_videos: string[] = [],
  ref_audios: string[] = [],
): string => {
  // 按类型分别计数，用于从对应的 ref 数组中取 ID
  const imageCounters = [0]; // 用数组包装以便在闭包中修改
  const videoCounters = [0];
  const audioCounters = [0];

  return prompt.replace(BARE_MENTION_RE, (_match, typeCN, numStr) => {
    const num = parseInt(numStr, 10);
    let id = '';
    if (typeCN === '图片') {
      imageCounters[0]++;
      // 用 num-1 作为索引优先，否则按出现顺序递增
      const idx = num - 1 < ref_images.length ? num - 1 : imageCounters[0] - 1;
      id = ref_images[idx] || '';
    } else if (typeCN === '视频') {
      videoCounters[0]++;
      const idx = num - 1 < ref_videos.length ? num - 1 : videoCounters[0] - 1;
      id = ref_videos[idx] || '';
    } else if (typeCN === '音频') {
      audioCounters[0]++;
      const idx = num - 1 < ref_audios.length ? num - 1 : audioCounters[0] - 1;
      id = ref_audios[idx] || '';
    }
    return `[@${typeCN}${numStr}:${id}]`;
  });
};
